import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Calendar, Euro, Eye, Mail, Hash, Copy, UserPlus, Link2, AlertTriangle, Ban, Trash2, FileX, Heart, Crown, ArrowRight, ShieldCheck, Baby, Info, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ContractWizard from '../components/ContractWizard';
import ContractDetailsModal from '../components/ContractDetailsModal';
import { ChildIcon, genderBg, genderColor, genderAccent, genderBorder } from '../components/ChildIcon';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contracts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [planLimits, setPlanLimits] = useState(null);
  
  // Join by code state (integrated for nannies)
  const [joinCode, setJoinCode] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  
  // Invitation state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteContract, setInviteContract] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteNannyName, setInviteNannyName] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null);
  
  const isParent = user?.role === 'parent';
  const isNanny = user?.role === 'nanny';
  const emailVerified = user?.email_verified !== false;
  
  // Contract actions state
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionContract, setActionContract] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  // Lazy Pajemploi verification for linked contracts
  // Re-runs if never checked (undefined) OR if last check found missing fields (profiles may have been updated since)
  useEffect(() => {
    if (!contracts.length) return;
    contracts.forEach(async (contract) => {
      const needsCheck = contract.nanny_user_id &&
        contract.status === 'active' &&
        (
          contract.pajemploi_eligible === undefined ||
          contract.pajemploi_eligible === null ||
          // Re-verify if last check found missing fields (user may have updated their profile)
          (contract.pajemploi_eligible === false && contract.pajemploi_verification?.missing_fields?.length > 0)
        );
      if (needsCheck) {
        try {
          const res = await axios.post(`${API}/contracts/${contract.id}/verify-pajemploi`);
          setContracts(prev => prev.map(c =>
            c.id === contract.id
              ? { ...c, pajemploi_eligible: res.data.pajemploi_eligible, pajemploi_verification: res.data }
              : c
          ));
        } catch (err) {
          console.warn('Contracts: pajemploi eligibility check failed', err);
        }
      }
    });
  }, [contracts.length]);
  
  const fetchData = async () => {
    try {
      const [contractsRes, limitsRes] = await Promise.all([
        axios.get(`${API}/contracts`),
        axios.get(`${API}/payments/limits/check`),
      ]);
      setContracts(contractsRes.data);
      setPlanLimits(limitsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  const handleWizardSuccess = () => {
    setWizardOpen(false);
    fetchData();
  };

  // === Join by Code (Nanny) ===
  const handleJoinByCode = async (e) => {
    e.preventDefault();
    if (joinCode.length !== 6) return;
    try {
      setJoinLoading(true);
      const response = await axios.post(`${API}/invitations/accept/code`, { code: joinCode.toUpperCase() });
      toast.success(response.data.message || 'Contrat lié avec succès !');
      setJoinCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code invalide ou expiré');
    } finally {
      setJoinLoading(false);
    }
  };
  
  const handleViewDetails = (contract) => {
    setSelectedContract(contract);
    setDetailsOpen(true);
  };
  
  // === Invitation Logic ===
  const openInviteModal = (contract) => {
    setInviteContract(contract);
    setInviteEmail('');
    setInviteNannyName('');
    setGeneratedCode(null);
    setInviteModalOpen(true);
  };
  
  const handleSendEmailInvite = async () => {
    if (!inviteEmail || !inviteContract) return;
    setInviteLoading(true);
    try {
      await axios.post(`${API}/invitations/email`, {
        contract_id: inviteContract.id,
        nanny_email: inviteEmail,
        nanny_name: inviteNannyName || undefined
      });
      toast.success(`Invitation envoyée à ${inviteEmail}`);
      setInviteModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setInviteLoading(false);
    }
  };
  
  const handleGenerateCode = async () => {
    if (!inviteContract) return;
    setInviteLoading(true);
    try {
      const res = await axios.post(`${API}/invitations/code`, {
        contract_id: inviteContract.id
      });
      setGeneratedCode(res.data.code);
      toast.success('Code généré !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération du code');
    } finally {
      setInviteLoading(false);
    }
  };
  
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copié !');
  };
  
  // Contract management actions
  const openTerminateDialog = (contract) => {
    setActionContract(contract);
    setTerminateDialogOpen(true);
  };
  
  const openDeleteDialog = (contract) => {
    setActionContract(contract);
    setDeleteDialogOpen(true);
  };
  
  const handleTerminate = async () => {
    if (!actionContract) return;
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/contracts/${actionContract.id}/terminate`);
      toast.success(res.data.message);
      setTerminateDialogOpen(false);
      setActionContract(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la résiliation');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDelete = async () => {
    if (!actionContract) return;
    setActionLoading(true);
    try {
      const res = await axios.delete(`${API}/contracts/${actionContract.id}`);
      toast.success(res.data.message);
      setDeleteDialogOpen(false);
      setActionContract(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="container-custom py-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="space-y-4">
          {[1, 2].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }
  
  const canCreateContract = !planLimits || (planLimits.contracts?.remaining > 0);
  const isPremium = planLimits?.is_premium;
  const contractCount = planLimits?.contracts?.current || contracts.filter(c => c.status === 'active').length;
  const maxContracts = planLimits?.contracts?.max || 1;
  
  return (
    <div className="container-custom py-6 space-y-6" data-testid="contracts-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)' }}>
              Contrats
            </h1>
            <p className="text-[var(--text-secondary)] mt-1">
              {isParent ? 'Gérez vos contrats d\'accueil et invitez vos nounous. Un contrat = un enfant.' : 'Vos contrats liés avec vos employeurs'}
            </p>
          </div>
          {/* Child counter badge */}
          {isParent && planLimits && (
            <Badge 
              variant="outline" 
              className={cn(
                "ml-2 text-sm px-3 py-1 flex items-center gap-1.5",
                isPremium 
                  ? "bg-[var(--secondary-400)]/10 text-[var(--secondary-400)] border-[var(--secondary-400)]" 
                  : contractCount >= maxContracts
                    ? "bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)]"
                    : "bg-[var(--primary-400)]/10 text-[var(--primary-400)] border-[var(--primary-400)]"
              )}
              data-testid="child-counter-badge"
            >
              <Baby className="h-3.5 w-3.5" />
              {contractCount}/{isPremium ? '∞' : maxContracts} enfant{contractCount > 1 ? 's' : ''}
              {isPremium && <Crown className="h-3 w-3 ml-0.5" />}
            </Badge>
          )}
        </div>
        
        {(isParent || (isNanny && isPremium)) && (
          <Button 
            className="rounded-full" 
            style={{ backgroundColor: (canCreateContract && emailVerified) ? 'var(--primary-400)' : undefined }}
            variant={(canCreateContract && emailVerified) ? 'default' : 'outline'}
            disabled={!canCreateContract || !emailVerified}
            onClick={() => {
              if (!emailVerified) {
                toast.error('Veuillez vérifier votre adresse email avant de créer un contrat.', {
                  description: 'Rendez-vous dans votre profil pour renvoyer le lien.',
                  action: { label: 'Mon profil', onClick: () => navigate('/profil') },
                });
                return;
              }
              if (canCreateContract) setWizardOpen(true);
            }}
            data-testid="create-contract-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau contrat
          </Button>
        )}
      </div>
      
      {/* Contract Wizard */}
      {(isParent || (isNanny && isPremium)) && (
        <ContractWizard 
          isOpen={wizardOpen} 
          onClose={() => setWizardOpen(false)}
          onContractCreated={handleWizardSuccess}
        />
      )}
      
      {/* Plan Limits Warning - Enhanced for multi-enfants */}
      {!isPremium && contractCount >= maxContracts && (
        <Card className="border-[var(--secondary-400)]/40 bg-gradient-to-r from-[var(--secondary-400)]/5 to-transparent" data-testid="plan-limit-warning">
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-4 py-4">
            <div className="flex items-center gap-3 shrink-0">
              <div className="h-10 w-10 rounded-full bg-[var(--secondary-400)]/10 flex items-center justify-center">
                <Crown className="h-5 w-5 text-[var(--secondary-400)]" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[var(--text-primary)]">
                Limite du plan gratuit atteinte
              </p>
              <p className="text-sm text-[var(--text-secondary)] mt-0.5">
                Le plan gratuit est limité à <strong>1 enfant</strong>. Passez à la version complète pour gérer <strong>tous vos enfants</strong> — un seul abonnement suffit pour en faire profiter toute votre collaboration.
              </p>
            </div>
            <Link to="/billing">
              <Button 
                style={{ backgroundColor: 'var(--secondary-400)' }}
                className="shrink-0 gap-2"
                data-testid="upgrade-from-contracts-btn"
              >
                <Crown className="h-4 w-4" />
                Passer à la version complète
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
      
      {contracts.length === 0 ? (
        <div className="space-y-6">
          {/* Empty state card */}
          <Card className="border-dashed" data-testid="empty-state-contracts">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--primary-50)' }}>
                {isParent || (isNanny && isPremium) ? <Baby className="h-8 w-8 text-[var(--secondary-400)]" /> : <Link2 className="h-8 w-8 text-[var(--primary-400)]" />}
              </div>
              <CardTitle style={{ fontFamily: 'var(--font-secondary)' }}>
                {isParent 
                  ? 'Créez votre premier contrat' 
                  : isPremium 
                    ? 'Créez ou rejoignez un contrat' 
                    : 'En attente d\'une invitation'}
              </CardTitle>
              <CardDescription className="mt-2 max-w-md mx-auto">
                {isParent 
                  ? 'Ajoutez les informations de votre enfant et invitez votre assistante maternelle. Un contrat = un enfant.'
                  : isPremium
                    ? 'Créez un contrat pour chaque enfant que vous gardez, ou rejoignez via un code d\'invitation du parent.'
                    : 'Votre employeur doit créer le contrat et vous inviter. Utilisez le formulaire ci-dessous pour rejoindre avec un code.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center gap-3 pt-2 pb-6">
              {(isParent || (isNanny && isPremium)) && (
                <Button 
                  onClick={() => setWizardOpen(true)}
                  style={{ backgroundColor: 'var(--primary-400)' }}
                  className="gap-2"
                  data-testid="empty-create-contract-btn"
                >
                  <Plus className="h-4 w-4" />
                  Créer un contrat
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Inline Join by Code for nannies */}
          {isNanny && (
            <Card data-testid="join-code-section">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary-100)' }}>
                    <Link2 className="h-5 w-5" style={{ color: 'var(--primary-400)' }} />
                  </div>
                  <div>
                    <CardTitle className="text-base" style={{ fontFamily: 'var(--font-secondary)' }}>Rejoindre via un code</CardTitle>
                    <CardDescription className="text-xs">Entrez le code à 6 caractères communiqué par le parent</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleJoinByCode} className="flex items-end gap-3">
                  <div className="flex-1 max-w-xs">
                    <Input
                      type="text"
                      placeholder="ABC123"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      maxLength={6}
                      minLength={6}
                      required
                      className="text-center text-xl font-mono font-bold tracking-widest"
                      data-testid="join-code-input"
                    />
                  </div>
                  <Button 
                    type="submit"
                    disabled={joinLoading || joinCode.length !== 6}
                    style={{ backgroundColor: 'var(--primary-400)' }}
                    className="gap-2 shrink-0"
                    data-testid="join-submit-button"
                  >
                    {joinLoading ? 'Liaison...' : <>Rejoindre <ArrowRight className="h-4 w-4" /></>}
                  </Button>
                </form>
                <p className="text-xs text-[var(--text-tertiary)] mt-2 flex items-center gap-1">
                  <Info className="h-3 w-3" /> Si vous n'avez pas reçu de code, demandez au parent de générer une invitation.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contracts.map(contract => (
            <Card key={contract.id} className="hover:shadow-md transition-smooth overflow-hidden" data-testid="contract-card">
              <CardHeader style={{ backgroundColor: genderBg(contract.child_sex) }}>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2" style={{ color: genderAccent(contract.child_sex) }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: genderBg(contract.child_sex), border: `1.5px solid ${genderColor(contract.child_sex)}` }}>
                      <ChildIcon sex={contract.child_sex} className="h-4 w-4" style={{ color: genderColor(contract.child_sex) }} />
                    </div>
                    {contract.child_first_name || contract.child_name}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {!contract.nanny_user_id && isParent && (
                      <Badge variant="outline" className="bg-[var(--warning-light)] text-[var(--warning)] border-[var(--warning)]">
                        Nounou non liée
                      </Badge>
                    )}
                    {!contract.parent_user_id && isNanny && (
                      <Badge variant="outline" className="bg-[var(--warning-light)] text-[var(--warning)] border-[var(--warning)]">
                        Parent non lié
                      </Badge>
                    )}
                    <Badge 
                      variant="outline" 
                      className={cn(
                        contract.status === 'active' && 'bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)]',
                        contract.status === 'terminated' && 'bg-[var(--error)]/10 text-[var(--error)] border-[var(--error)]',
                        contract.status === 'inactive' && 'bg-[var(--neutral-200)] text-[var(--text-tertiary)]'
                      )}
                    >
                      {contract.status === 'active' ? 'Actif' : contract.status === 'terminated' ? 'Résilié' : 'Inactif'}
                    </Badge>
                  </div>
                </div>
                {/* Show linked persons info */}
                {contract.parent_info && isNanny && (
                  <CardDescription>Parent : {contract.parent_info.full_name}</CardDescription>
                )}
                {contract.nanny_info && isParent && (
                  <CardDescription>Nounou : {contract.nanny_info.full_name}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-3 pt-5">
                {/* Pajemploi eligibility badge */}
                {contract.nanny_user_id && contract.status === 'active' && contract.pajemploi_eligible === true && (
                  <div className="flex items-center justify-between p-2.5 rounded-lg bg-[var(--success-light)] border border-[var(--success)]">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-[var(--success)]" />
                      <span className="text-xs font-semibold text-[var(--success)]">Pajemploi activé</span>
                    </div>
                    {isParent && !['premium', 'parent_pro', 'nanny_pro'].includes(user?.current_plan) && (
                      <Link to="/billing">
                        <Badge className="cursor-pointer gap-1 text-[10px] px-2 py-0.5" style={{ backgroundColor: 'var(--primary-400)', color: 'white' }} data-testid="pajemploi-upgrade-cta">
                          <Crown className="h-3 w-3" /> Passer Pro
                        </Badge>
                      </Link>
                    )}
                  </div>
                )}
                {contract.nanny_user_id && contract.status === 'active' && contract.pajemploi_eligible === false && (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-[var(--warning-light)] border border-[var(--warning)]">
                    <AlertTriangle className="h-4 w-4 text-[var(--warning)] shrink-0" />
                    <span className="text-xs text-[var(--warning-dark)]">
                      {contract.pajemploi_verification?.missing_fields?.length > 0
                        ? `Pajemploi : profils incomplets (${contract.pajemploi_verification.missing_fields.join(', ')})`
                        : contract.pajemploi_verification?.errors?.length > 0
                          ? `Pajemploi : ${contract.pajemploi_verification.errors[0]}`
                          : contract.pajemploi_verification?.reason
                            ? `Pajemploi : ${contract.pajemploi_verification.reason}`
                            : 'Vérification Pajemploi en attente'}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                  <Euro className="h-4 w-4" />
                  <span className="font-mono font-semibold text-[var(--primary-400)]">{contract.hourly_rate}€/h</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                  <Calendar className="h-4 w-4" />
                  Depuis le {new Date(contract.start_date).toLocaleDateString('fr-FR')}
                </div>
                {contract.schedule_notes && (
                  <p className="text-sm text-[var(--text-secondary)] mt-2 pt-2 border-t border-[var(--neutral-200)]">
                    {contract.schedule_notes}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[var(--neutral-200)]">
                  <Button
                    onClick={() => handleViewDetails(contract)}
                    variant="outline"
                    size="sm"
                    data-testid="view-details-button"
                  >
                    <Eye className="mr-1 h-4 w-4" />
                    Détails
                  </Button>
                  
                  <Link to={`/contrats/${contract.id}/sante`}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-[#E17A73] border-[#E17A73]/40 hover:bg-[#E17A73]/5"
                      data-testid="child-health-button"
                    >
                      <Heart className="mr-1 h-4 w-4" />
                      Santé
                    </Button>
                  </Link>
                  
                  {/* Parent: invite nanny if not linked */}
                  {isParent && !contract.nanny_user_id && (
                    <Button
                      onClick={() => openInviteModal(contract)}
                      size="sm"
                      style={{ backgroundColor: 'var(--secondary-400)' }}
                      data-testid="invite-nanny-button"
                    >
                      <UserPlus className="mr-1 h-4 w-4" />
                      Inviter nounou
                    </Button>
                  )}
                  
                  {contract.pending_invitation && isParent && !contract.nanny_user_id && (
                    <Badge variant="outline" className="bg-[var(--info-light)] text-[var(--info)] border-[var(--info)] text-xs">
                      Invitation en attente
                    </Badge>
                  )}
                  
                  {/* Contract management actions (Parent only) */}
                  {isParent && contract.status === 'active' && (
                    <Button
                      onClick={() => openTerminateDialog(contract)}
                      variant="outline"
                      size="sm"
                      className="text-[var(--warning)] border-[var(--warning)]/40 hover:bg-[var(--warning)]/5"
                      data-testid="terminate-contract-button"
                    >
                      <Ban className="mr-1 h-4 w-4" />
                      Résilier
                    </Button>
                  )}
                  
                  {isParent && (
                    <Button
                      onClick={() => openDeleteDialog(contract)}
                      variant="outline"
                      size="sm"
                      className="text-[var(--error)] border-[var(--error)]/30 hover:bg-[var(--error)]/5"
                      data-testid="delete-contract-button"
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      Supprimer
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Contract Details Modal */}
      <ContractDetailsModal
        isOpen={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedContract(null);
        }}
        contract={selectedContract}
      />
      
      {/* Invite Nanny Modal */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent className="sm:max-w-md bg-[var(--bg-primary)]" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="invite-nanny-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-secondary)' }}>
              <UserPlus className="h-5 w-5 text-[var(--secondary-400)]" />
              Inviter une nounou
            </DialogTitle>
            <DialogDescription>
              Invitez votre assistante maternelle pour le contrat de <strong>{inviteContract?.child_first_name || inviteContract?.child_name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-auto p-1" style={{ backgroundColor: 'var(--neutral-100)' }}>
              <TabsTrigger value="email" className="gap-1 text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm">
                <Mail className="h-4 w-4" />
                Par email
              </TabsTrigger>
              <TabsTrigger value="code" className="gap-1 text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm">
                <Hash className="h-4 w-4" />
                Par code
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="email" className="space-y-4 mt-4">
              <div>
                <Label htmlFor="nanny_email">Email de la nounou *</Label>
                <Input
                  id="nanny_email"
                  type="email"
                  placeholder="nounou@email.fr"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <Label htmlFor="nanny_name">Nom de la nounou (optionnel)</Label>
                <Input
                  id="nanny_name"
                  type="text"
                  placeholder="Prénom Nom"
                  value={inviteNannyName}
                  onChange={(e) => setInviteNannyName(e.target.value)}
                  data-testid="invite-name-input"
                />
              </div>
              <Button
                onClick={handleSendEmailInvite}
                disabled={inviteLoading || !inviteEmail}
                className="w-full"
                style={{ backgroundColor: 'var(--secondary-400)' }}
                data-testid="send-invite-email-btn"
              >
                {inviteLoading ? 'Envoi en cours...' : 'Envoyer l\'invitation par email'}
              </Button>
              <p className="text-xs text-[var(--text-tertiary)]">
                La nounou recevra un email avec un lien pour créer son compte et se lier au contrat.
              </p>
            </TabsContent>
            
            <TabsContent value="code" className="space-y-4 mt-4">
              {!generatedCode ? (
                <>
                  <p className="text-sm text-[var(--text-secondary)]">
                    Générez un code à 6 caractères que vous pourrez communiquer oralement ou par SMS à votre nounou.
                  </p>
                  <Button
                    onClick={handleGenerateCode}
                    disabled={inviteLoading}
                    className="w-full"
                    style={{ backgroundColor: 'var(--primary-400)' }}
                    data-testid="generate-code-btn"
                  >
                    {inviteLoading ? 'Génération...' : 'Générer un code d\'invitation'}
                  </Button>
                </>
              ) : (
                <div className="text-center space-y-4">
                  <p className="text-sm font-medium">Code d'invitation :</p>
                  <div className="flex items-center justify-center gap-3">
                    <div 
                      className="text-3xl font-mono font-bold px-6 py-3 rounded-lg" 
                      style={{ backgroundColor: 'var(--primary-400)', color: 'white', letterSpacing: '0.3em' }}
                      data-testid="generated-invite-code"
                    >
                      {generatedCode}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyCode(generatedCode)}
                      data-testid="copy-invite-code-btn"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-[var(--text-tertiary)]">
                    Communiquez ce code à votre nounou. Elle pourra l'entrer dans la page "Rejoindre" de son espace Nannyou.
                    Ce code expire dans 7 jours.
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      
      {/* ═══ Join by Code section for nannies (when contracts exist) ═══ */}
      {isNanny && contracts.length > 0 && (
        <Card className="mt-6" data-testid="join-code-section">
          <CardContent className="pt-5">
            <form onSubmit={handleJoinByCode} className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 shrink-0">
                <Link2 className="h-4 w-4 text-[var(--primary-400)]" />
                <span className="text-sm font-medium text-[var(--text-primary)]">Rejoindre un contrat :</span>
              </div>
              <Input
                type="text"
                placeholder="ABC123"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                maxLength={6}
                minLength={6}
                className="text-center text-base font-mono font-bold tracking-widest w-36"
                data-testid="join-code-input-inline"
              />
              <Button 
                type="submit"
                size="sm"
                disabled={joinLoading || joinCode.length !== 6}
                style={{ backgroundColor: 'var(--primary-400)' }}
                className="gap-1.5 shrink-0"
                data-testid="join-submit-button-inline"
              >
                {joinLoading ? 'Liaison...' : <>Rejoindre <ArrowRight className="h-3.5 w-3.5" /></>}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
      
      {/* Terminate Contract Dialog */}
      <Dialog open={terminateDialogOpen} onOpenChange={setTerminateDialogOpen}>
        <DialogContent className="bg-[var(--bg-primary)]" data-testid="terminate-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[var(--warning)]" style={{ fontFamily: 'var(--font-secondary)' }}>
              <Ban className="h-5 w-5" />
              Résilier le contrat
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>
                Vous êtes sur le point de résilier le contrat pour <strong>{actionContract?.child_first_name || actionContract?.child_name}</strong>.
              </p>
              <ul className="text-sm space-y-2 text-[var(--text-secondary)]">
                <li className="flex items-start gap-2">
                  <FileX className="h-4 w-4 text-[var(--warning)] mt-0.5 shrink-0" />
                  <span>Le contrat passera au statut <strong>"Résilié"</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-[var(--text-tertiary)] mt-0.5 shrink-0" />
                  <span>L'historique du pointage et les données de paie seront conservés</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-[var(--warning)] mt-0.5 shrink-0" />
                  <span>La nounou liée sera notifiée de la résiliation</span>
                </li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setTerminateDialogOpen(false)} data-testid="terminate-cancel-btn">
              Annuler
            </Button>
            <Button 
              onClick={handleTerminate}
              disabled={actionLoading}
              className="bg-[var(--warning)] hover:bg-[var(--warning)]/90 text-white"
              data-testid="terminate-confirm-btn"
            >
              {actionLoading ? 'Résiliation...' : 'Confirmer la résiliation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Contract Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[var(--bg-primary)]" data-testid="delete-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[var(--error)]" style={{ fontFamily: 'var(--font-secondary)' }}>
              <Trash2 className="h-5 w-5" />
              Supprimer le contrat définitivement
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              <p>
                Vous êtes sur le point de <strong className="text-[var(--error)]">supprimer définitivement</strong> le contrat pour <strong>{actionContract?.child_first_name || actionContract?.child_name}</strong>.
              </p>
              <div className="p-3 rounded-lg bg-[var(--error)]/5 border border-[var(--error)]/20">
                <p className="text-sm font-medium text-[var(--error)]">
                  ⚠️ Cette action est irréversible. Seront supprimés :
                </p>
                <ul className="text-sm mt-2 space-y-1 text-[var(--text-secondary)]">
                  <li>• Le contrat et ses paramètres</li>
                  <li>• Tout l'historique du pointage</li>
                  <li>• Les déclarations de paie</li>
                  <li>• Les invitations liées</li>
                  <li>• Les demandes de congés</li>
                </ul>
              </div>
              <div className="p-3 rounded-lg bg-[var(--warning-light)] border border-[var(--warning)]/30">
                <p className="text-sm font-medium text-[var(--warning-dark)]">
                  📋 Le mandat Pajemploi de tierce déclaration sera également révoqué pour ce contrat. Un nouveau mandat sera automatiquement créé lors de la prochaine déclaration.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} data-testid="delete-cancel-btn">
              Annuler
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading}
              data-testid="delete-confirm-btn"
            >
              {actionLoading ? 'Suppression...' : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contracts;
